/**
 * Badge de `EstadoConfig` compartilhado entre os cards de domínio e as
 * linhas de resultado da busca do hub (T6) — a mesma pergunta ("qual o
 * estado deste item?") tem uma resposta visual só, em vez de o card e a
 * busca inventarem cada um a sua.
 *
 * Vocabulário e regras herdados de `DomainPage.tsx` (`RailIndicator`), que já
 * resolveu este problema para o rail de âncoras: `indisponivel` mostra «—»
 * (honestidade, não chute); item sem resolver (`estado` null/undefined) não
 * desenha nada (mesma regra); `locked` sem `tierLabel` ganha um cadeado
 * neutro com `role="img"` em vez de ficar mudo. Não foi extraído para um
 * módulo comum aos dois arquivos nesta task para não tocar `DomainPage.tsx`
 * fora do escopo do brief — mas a bitola visual é deliberadamente a mesma.
 */
import { Lock } from 'lucide-react';

import type { ConfigCatalogItem } from './catalog';
import type { EstadoConfig } from './estado-resolver';

export function EstadoBadge({
  estado,
  item,
}: {
  readonly estado: EstadoConfig | null | undefined;
  readonly item: Pick<ConfigCatalogItem, 'tierLabel'>;
}): JSX.Element | null {
  if (estado === 'locked') {
    if (item.tierLabel !== undefined) {
      return (
        <span className="ml-auto flex-shrink-0 rounded bg-accent/10 px-1.5 py-0.5 text-[10px] font-medium text-accent">
          {item.tierLabel}
        </span>
      );
    }
    return (
      <span role="img" aria-label="Bloqueado pelo plano atual" className="ml-auto flex-shrink-0">
        <Lock aria-hidden className="h-3 w-3 text-text-secondary" />
      </span>
    );
  }
  if (estado === 'soon') {
    return (
      <span className="ml-auto flex-shrink-0 rounded border border-dashed border-border-subtle px-1.5 py-0.5 text-[10px] text-text-secondary">
        Em breve
      </span>
    );
  }
  if (estado === 'ligado') {
    return (
      <span className="ml-auto flex flex-shrink-0 items-center gap-1 text-[11px] text-text-secondary">
        <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-green-500" />
        ligado
      </span>
    );
  }
  if (estado === 'desligado') {
    return (
      <span className="ml-auto flex flex-shrink-0 items-center gap-1 text-[11px] text-text-secondary">
        <span aria-hidden className="h-1.5 w-1.5 rounded-full border border-neutral-400" />
        desligado
      </span>
    );
  }
  if (estado === 'nao-configurado') {
    return (
      <span className="ml-auto flex flex-shrink-0 items-center gap-1 text-[11px] text-amber-700">
        <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-amber-500" />
        não configurada
      </span>
    );
  }
  if (estado === 'indisponivel') {
    return (
      <span
        className="ml-auto flex-shrink-0 text-[11px] text-text-secondary"
        aria-label="Estado indisponível"
      >
        —
      </span>
    );
  }
  // `estado` null/undefined: item sem resolver — nenhum badge (mesma regra do RailIndicator).
  return null;
}
